import dayjs from 'dayjs'
import { PremiumContribution } from 'src/models/PremiumContribution'
import useSWR, { SWRConfiguration } from 'swr'
import { fetcher } from './client'
import {
  GET_PREMIUM_CONTRIBUTIONS,
  GET_PREMIUM_CONTRIBUTIONS_SEARCH,
  getPremiumContribution,
} from './endpoints'
import {
  PremiumContributionsResponse,
  ServerPremiumContribution,
} from './types/premiumContribution'

export type PremiumContibutionsData = {
  premiumContributions: PremiumContribution[]
  totalCount: number
}

// 検索API用のデータ型
export type PremiumContibutionsSearchData = {
  premiumContributions: PremiumContribution[]
  totalCount: number
  availableCount: number
  contributionKeywords: Array<{ keyword: string }>
  isLast: boolean
  onePageDisplayItems: number
  keywordSearchCount: number
}

export const toPremiumContribution = (
  data: ServerPremiumContribution,
): PremiumContribution => ({
  id: data.id,
  companyId: data.companyId,
  title: data.title,
  content: data.content,
  status: data.status,
  purpose: data.purpose,
  startAt: dayjs(data.startAt),
  endAt: dayjs(data.endAt),
  isAnonymous: data.isAnonymous,
  employeeNumberText: data.employeeNumberText,
  salesScaleText: data.salesScaleText,
  daysLeftText: data.daysLeftText,
  companyName: data.companyName,
  companyAdminName: data.companyAdminName,
  shouldDisplayCloseLabel: data.shouldDisplayCloseLabel,
  isApplied: data.isApplied,
  displayBudgetText: data.displayBudgetText,
  positionText: data.positionText,
  contentTags: data.contentTags || [],
  typeOfBusiness: data.typeOfBusiness,
})
// 既存のAPI用のフェッチャー（後方互換性のため）
const premiumContributionsFetcher = async (
  url: string,
): Promise<PremiumContibutionsData> => {
  const res: PremiumContributionsResponse = await fetcher(url)
  return {
    premiumContributions: res.premiumContributions.map(toPremiumContribution),
    totalCount: res.totalCount,
  }
}

// 検索API用のフェッチャー
const premiumContributionsSearchFetcher = async (
  url: string,
): Promise<PremiumContibutionsSearchData> => {
  const res: PremiumContributionsResponse = await fetcher(url)
  return {
    premiumContributions: res.premiumContributions.map(toPremiumContribution),
    totalCount: res.totalCount,
    availableCount: res.premiumContributionAvailableCount,
    contributionKeywords: res.contributionKeywords,
    isLast: res.isLast,
    onePageDisplayItems: res.onePageDisplayItems,
    keywordSearchCount: res.keywordSearchCount,
  }
}

// 個別取得用のフェッチャー
const premiumContributionFetcher = async (
  url: string,
): Promise<PremiumContribution | undefined> => {
  const res: { premiumContribution: ServerPremiumContribution } = await fetcher(
    url,
  )
  return res.premiumContribution
    ? toPremiumContribution(res.premiumContribution)
    : undefined
}

export const usePremiumContributions = (
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<PremiumContibutionsData>(
    `${GET_PREMIUM_CONTRIBUTIONS}`,
    premiumContributionsFetcher,
    config,
  )

export const usePremiumContributionsSearch = (
  searchParams: PremiumContributionSearchParams,
  config: Partial<SWRConfiguration> = {},
) => {
  const { keyword, contentTag, status, entryStatus, page = 1 } = searchParams

  const queryParams = new URLSearchParams()
  if (keyword?.trim()) queryParams.set('keyword', keyword.trim())
  if (contentTag) queryParams.set('content_tag', contentTag)
  if (status) queryParams.set('status', status)
  if (entryStatus) queryParams.set('entry_status', entryStatus)
  queryParams.set('page', page.toString())

  const queryString = queryParams.toString()

  return useSWR<PremiumContibutionsSearchData>(
    `${GET_PREMIUM_CONTRIBUTIONS_SEARCH}?${queryString}`,
    premiumContributionsSearchFetcher,
    config,
  )
}

export const usePremiumContribution = (
  id: number,
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<PremiumContribution | undefined>(
    getPremiumContribution(id),
    premiumContributionFetcher,
    config,
  )

export type PremiumContributionSearchParams = {
  keyword?: string
  contentTag?: string
  status?: string
  entryStatus?: string
  page?: number
}
